// Typy dla faktur proforma
export interface ProformaInvoice {
  id: string;
  invoiceNumber: string; // Format: FP/RRRR/MM/NNN
  issueDate: string;
  saleDate: string; // Data sprzedaży
  validUntil?: string; // Data ważności (opcjonalna)
  paymentDeadline: string;
  paymentMethod: string;
  status: ProformaInvoiceStatus;
  
  // Dane nabywcy/klienta
  clientName: string;
  clientAddress: string;
  clientPostalCode: string;
  clientCity: string;
  clientNIP: string;
  clientEmail?: string;
  clientPhone?: string;
  
  // Dla kompatybilności wstecznej
  buyer?: {
    name: string;
    address: string;
    postalCode: string;
    city: string;
    nip?: string;
    email?: string;
    phone?: string;
  };
  
  // Pozycje faktury
  items: ProformaInvoiceItem[];
  
  // Podsumowanie
  totalNet: number;
  totalVat: number;
  totalGross: number;
  
  // Dla kompatybilności wstecznej
  summary?: {
    netTotal: number;
    vatTotal: number;
    grossTotal: number;
    grossTotalInWords: string;
  };
  
  notes?: string;
  internalNotes?: string;
  
  // Metadane
  qualityCheckId?: string; // powiązanie z kontrolą jakości
  qualityControlId?: string; // alias dla qualityCheckId
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  pdfUrl?: string;
  emailSentAt?: string;
}

export interface ProformaInvoiceItem {
  id?: string;
  name?: string; // dla kompatybilności
  description: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  netPrice?: number; // dla kompatybilności
  vatRate: number;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  
  // Dla kompatybilności wstecznej
  netValue?: number;
  vatValue?: number;
  grossValue?: number;
}

// Status faktury
export type ProformaInvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled' | 'overdue' | 'pending';

// Dane firmy wystawiającej faktury
export const COMPANY_DATA = {
  name: 'PPHU Plexi System s.c.',
  address: 'ul. Ks. Dr. Leona Heyke 11',
  postalCode: '84-206',
  city: 'Nowy Dwór Wejherowski',
  nip: '5882396272',
  regon: '221645384',
  phone: '+884 042 107',
  email: 'biuro@plexisystem.pl',
  website: 'www.plexisystem.pl',
  bank: {
    name: 'mBank',
    accountNumber: '14 1140 2004 0000 3802 7420 9310',
    swift: 'BREXPLPWMBK'
  }
} as const;

// Dostępne stawki VAT
export const VAT_RATES = [
  { value: 0.23, label: '23%' },
  { value: 0.08, label: '8%' },
  { value: 0.05, label: '5%' },
  { value: 0, label: '0%' },
  { value: -1, label: 'zw.' }
] as const;

// Formularz faktury
export interface ProformaInvoiceFormData {
  issueDate: string;
  saleDate: string;
  paymentDeadline: string;
  paymentMethod: string;
  
  // Dane klienta
  clientName: string;
  clientAddress: string;
  clientPostalCode: string;
  clientCity: string;
  clientNIP: string;
  clientEmail?: string;
  clientPhone?: string;
  
  // Dla kompatybilności wstecznej
  buyer?: ProformaInvoice['buyer'];
  
  // Pozycje
  items: Omit<ProformaInvoiceItem, 'id' | 'netAmount' | 'vatAmount' | 'grossAmount'>[];
  
  notes?: string;
  internalNotes?: string;
  qualityControlId?: string;
  
  // Dla kompatybilności wstecznej
  paymentDays?: number;
  validityDays?: number;
}

// Filtrowanie i sortowanie
export interface ProformaInvoiceFilters {
  status?: ProformaInvoiceStatus | 'all';
  startDate?: string;
  endDate?: string;
  searchTerm?: string;
  
  // Dla kompatybilności wstecznej
  buyerName?: string;
  invoiceNumber?: string;
}

export type ProformaInvoiceSortBy = 'issueDate' | 'invoiceNumber' | 'buyerName' | 'grossTotal' | 'status';
export type SortDirection = 'asc' | 'desc';

// Nowe typy dla szablonów
export interface ProformaTemplate {
  id: string;
  name: string;
  description?: string;
  clientName: string;
  clientAddress: string;
  clientPostalCode: string;
  clientCity: string;
  clientNIP: string;
  clientEmail?: string;
  clientPhone?: string;
  paymentMethod: string;
  paymentDays: number;
  notes?: string;
  items: ProformaTemplateItem[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProformaTemplateItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
}

export interface ProformaEmailQueue {
  id: string;
  invoiceId: string;
  recipientEmail: string;
  subject: string;
  body: string;
  status: 'pending' | 'sent' | 'failed';
  sentAt?: string;
  error?: string;
  createdAt: string;
  createdBy: string;
}

export interface ProformaInvoiceHistory {
  id: string;
  invoiceId: string;
  action: string;
  changes: Record<string, any>;
  createdAt: string;
  createdBy: string;
}

export interface ProformaStatistics {
  total: number;
  byStatus: {
    draft: number;
    sent: number;
    paid: number;
    cancelled: number;
    overdue: number;
  };
  totalValue: {
    net: number;
    vat: number;
    gross: number;
  };
}
